import { useState } from 'react';
import { api, ApiError, formatDate, scr } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { loadList, upsertList } from '../../lib/localList';
import type {
  ChecklistItem,
  MeansTest,
  MeansTestComputation,
  WelfareApplication,
  WelfareAward,
  WelfareCaseload,
} from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, Spinner, StatCard, StatusBadge } from '../../ui/components';

const DISTRICT_ROLES = ['DISTRICT_WELFARE_OFFICER', 'ASP_SENIOR_OFFICER', 'ASP_ADMIN'];
const SENIOR_ROLES = ['ASP_SENIOR_OFFICER', 'ASP_ADMIN'];

const DEFAULT_CHECKLIST: ChecklistItem[] = [
  { item: 'NIN / ID copy', present: false },
  { item: 'Household composition declaration', present: false },
  { item: 'Proof of income (or nil-income declaration)', present: false },
];

/**
 * S03 has no `GET /applications` list — this page tracks the applications
 * this browser has moved through the pipeline (each entry is a real API
 * response) alongside two genuine list endpoints: `GET /caseload/:district`
 * and `GET /reviews/due`.
 */
export function WelfareAssessment() {
  const { persona } = useAuth();
  const isDistrict = persona?.roles.some((r) => DISTRICT_ROLES.includes(r)) ?? false;
  const isSenior = persona?.roles.some((r) => SENIOR_ROLES.includes(r)) ?? false;
  const isAdmin = persona?.roles.includes('ASP_ADMIN') ?? false;
  const cacheKey = 'welfare.applications';

  const [applications, setApplications] = useState<WelfareApplication[]>(() => loadList<WelfareApplication>(cacheKey));
  const [computations, setComputations] = useState<Record<string, MeansTestComputation>>({});
  const [districtId, setDistrictId] = useState(persona?.districtId ?? 'baie-lazare');
  const [showNewApp, setShowNewApp] = useState(false);
  const [showThresholds, setShowThresholds] = useState(false);
  const [meansTestTarget, setMeansTestTarget] = useState<WelfareApplication | null>(null);
  const [approveTarget, setApproveTarget] = useState<WelfareApplication | null>(null);
  const [reviewTarget, setReviewTarget] = useState<WelfareAward | null>(null);
  const [error, setError] = useState<string | null>(null);

  const caseloadQ = useApiQuery<WelfareCaseload>(isDistrict ? `/api/v1/welfare/caseload/${districtId}` : null);
  const reviewsQ = useApiQuery<{ items: WelfareAward[] }>(isDistrict ? '/api/v1/welfare/reviews/due' : null);

  if (!isDistrict) {
    return (
      <div>
        <PageHeader title="Welfare Assessment" />
        <ErrorNote message="This view requires the District Welfare Officer, Senior Officer, or ASP Administrator role." />
      </div>
    );
  }

  const upsert = (a: WelfareApplication) => setApplications(upsertList(cacheKey, a));

  const docsComplete = async (a: WelfareApplication) => {
    setError(null);
    try {
      const updated = await api.post<WelfareApplication>(`/api/v1/welfare/applications/${a.id}/docs-complete`);
      upsert({ ...a, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code}${e.details ? ` — missing: ${JSON.stringify(e.details)}` : ''})` : 'Failed');
    }
  };

  const recommend = async (a: WelfareApplication) => {
    setError(null);
    try {
      const updated = await api.post<WelfareApplication>(`/api/v1/welfare/applications/${a.id}/recommend`);
      upsert({ ...a, ...updated });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed');
    }
  };

  return (
    <div>
      <PageHeader
        title="Welfare Assistance (means-tested)"
        subtitle="Application → household means test → award with review date & conditionality"
        actions={
          <>
            {isAdmin ? (
              <button className="btn-secondary" onClick={() => setShowThresholds(true)}>
                Set thresholds
              </button>
            ) : null}
            <button className="btn-primary" onClick={() => setShowNewApp(true)}>
              + New application
            </button>
          </>
        }
      />
      {error ? (
        <div className="mb-4">
          <ErrorNote message={error} />
        </div>
      ) : null}

      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <Field label="District" className="sm:col-span-1">
          <input className="field-input" value={districtId} onChange={(e) => setDistrictId(e.target.value)} />
        </Field>
        {caseloadQ.loading ? (
          <Spinner />
        ) : (
          <>
            <StatCard label="Open applications" value={caseloadQ.data?.openApplications ?? 0} />
            <StatCard label="Active awards" value={caseloadQ.data?.activeAwards ?? 0} hint={`${scr(caseloadQ.data?.monthlyCommitment ?? 0)}/mo`} />
          </>
        )}
      </div>

      <div className="mb-6">
        <h2 className="mb-2 text-sm font-bold text-slate-700">Applications in progress</h2>
        <div className="card overflow-x-auto">
          {applications.length === 0 ? (
            <EmptyState title="No applications yet" hint="Start a new application against a registered household." />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Reference</th>
                  <th className="table-head">Household</th>
                  <th className="table-head">Status</th>
                  <th className="table-head">Means test</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody>
                {applications.map((a) => {
                  const computed = computations[a.id];
                  return (
                    <tr key={a.id}>
                      <td className="table-cell font-medium">{a.refNumber}</td>
                      <td className="table-cell text-xs">{a.householdId}</td>
                      <td className="table-cell">
                        <StatusBadge status={a.status} />
                      </td>
                      <td className="table-cell text-xs">
                        {computed ? `Award ${scr(computed.officerOverride ?? computed.computedAward)}` : a.meansTest ? `Award ${scr(a.meansTest.computedAward)}` : '—'}
                      </td>
                      <td className="table-cell text-right">
                        {a.status === 'APPLIED' ? (
                          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => docsComplete(a)}>
                            Docs complete
                          </button>
                        ) : a.status === 'DOCS_COMPLETE' ? (
                          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => setMeansTestTarget(a)}>
                            Run means test
                          </button>
                        ) : a.status === 'MEANS_TESTED' ? (
                          <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => recommend(a)}>
                            Recommend
                          </button>
                        ) : a.status === 'OFFICER_RECOMMENDED' && isSenior ? (
                          <button className="btn-primary !px-2 !py-1 text-xs" onClick={() => setApproveTarget(a)}>
                            Approve
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div>
        <h2 className="mb-2 text-sm font-bold text-slate-700">Reviews due</h2>
        <div className="card overflow-x-auto">
          {reviewsQ.loading ? (
            <Spinner />
          ) : (reviewsQ.data?.items.length ?? 0) === 0 ? (
            <EmptyState title="No reviews due" />
          ) : (
            <table className="w-full">
              <thead>
                <tr>
                  <th className="table-head">Award</th>
                  <th className="table-head text-right">Monthly</th>
                  <th className="table-head">Review date</th>
                  <th className="table-head" />
                </tr>
              </thead>
              <tbody>
                {reviewsQ.data!.items.map((aw) => (
                  <tr key={aw.id}>
                    <td className="table-cell text-xs">{aw.id}</td>
                    <td className="table-cell text-right tabular-nums">{scr(aw.monthly)}</td>
                    <td className="table-cell text-xs text-slate-500">{formatDate(aw.reviewDate)}</td>
                    <td className="table-cell text-right">
                      <button className="btn-secondary !px-2 !py-1 text-xs" onClick={() => setReviewTarget(aw)}>
                        Log visit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showNewApp ? (
        <NewApplicationModal
          onClose={() => setShowNewApp(false)}
          onDone={(a) => {
            upsert(a);
            setShowNewApp(false);
          }}
        />
      ) : null}
      {showThresholds ? <ThresholdsModal onClose={() => setShowThresholds(false)} /> : null}
      {meansTestTarget ? (
        <MeansTestModal
          application={meansTestTarget}
          onClose={() => setMeansTestTarget(null)}
          onDone={(computation) => {
            setComputations((c) => ({ ...c, [meansTestTarget.id]: computation }));
            upsert({
              ...meansTestTarget,
              status: 'MEANS_TESTED',
              meansTest: {
                id: `${meansTestTarget.id}:mt`,
                applicationId: meansTestTarget.id,
                householdSize: computation.householdSize,
                householdIncome: String(computation.householdIncome),
                thresholdApplied: String(computation.thresholdApplied),
                gap: String(computation.gap),
                computedAward: String(computation.computedAward),
                officerOverride: computation.officerOverride !== null ? String(computation.officerOverride) : null,
              } as MeansTest,
            });
            setMeansTestTarget(null);
          }}
        />
      ) : null}
      {approveTarget ? (
        <ApproveModal
          application={approveTarget}
          onClose={() => setApproveTarget(null)}
          onDone={(a) => {
            upsert(a);
            setApproveTarget(null);
          }}
        />
      ) : null}
      {reviewTarget ? (
        <ReviewModal
          award={reviewTarget}
          onClose={() => setReviewTarget(null)}
          onDone={() => {
            setReviewTarget(null);
            reviewsQ.reload();
          }}
        />
      ) : null}
    </div>
  );
}

function NewApplicationModal({ onClose, onDone }: { onClose: () => void; onDone: (a: WelfareApplication) => void }) {
  const [householdId, setHouseholdId] = useState('');
  const [checklist, setChecklist] = useState<ChecklistItem[]>(DEFAULT_CHECKLIST);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const app = await api.post<WelfareApplication>('/api/v1/welfare/applications', { householdId, checklist });
      onDone(app);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not open application');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="New welfare application" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Household ID (from the Registry)">
          <input className="field-input" value={householdId} onChange={(e) => setHouseholdId(e.target.value)} />
        </Field>
        <div>
          <div className="field-label">Intake checklist</div>
          {checklist.map((c, i) => (
            <label key={c.item} className="flex items-center gap-2 py-0.5 text-sm">
              <input
                type="checkbox"
                checked={c.present}
                onChange={() => setChecklist(checklist.map((x, j) => (j === i ? { ...x, present: !x.present } : x)))}
              />
              {c.item}
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !householdId}>
            {busy ? 'Opening…' : 'Open application'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function MeansTestModal({
  application,
  onClose,
  onDone,
}: {
  application: WelfareApplication;
  onClose: () => void;
  onDone: (c: MeansTestComputation) => void;
}) {
  const [asOf, setAsOf] = useState(() => new Date().toISOString().slice(0, 10));
  const [officerOverride, setOfficerOverride] = useState('');
  const [overrideReason, setOverrideReason] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ computation: MeansTestComputation }>(`/api/v1/welfare/applications/${application.id}/means-test`, {
        asOf,
        officerOverride: officerOverride ? Number.parseFloat(officerOverride) : undefined,
        overrideReason: officerOverride ? overrideReason || undefined : undefined,
      });
      onDone(r.computation);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Means test failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Run means test — ${application.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="As of date">
          <input className="field-input" type="date" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
        </Field>
        <Field label="Officer override amount (optional, SCR/month)">
          <input className="field-input" type="number" value={officerOverride} onChange={(e) => setOfficerOverride(e.target.value)} />
        </Field>
        {officerOverride ? (
          <Field label="Override reason">
            <input className="field-input" value={overrideReason} onChange={(e) => setOverrideReason(e.target.value)} />
          </Field>
        ) : null}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy}>
            {busy ? 'Computing…' : 'Compute'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ApproveModal({ application, onClose, onDone }: { application: WelfareApplication; onClose: () => void; onDone: (a: WelfareApplication) => void }) {
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [reviewAfterMonths, setReviewAfterMonths] = useState(6);
  const [conditions, setConditions] = useState('School attendance');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [award, setAward] = useState<WelfareAward | null>(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<{ application: WelfareApplication; award: WelfareAward }>(`/api/v1/welfare/applications/${application.id}/approve`, {
        startDate,
        reviewAfterMonths,
        conditions: conditions.split(',').map((s) => s.trim()).filter(Boolean),
      });
      setAward(r.award);
      onDone({ ...application, ...r.application });
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Approval failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Approve — ${application.refNumber}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {award ? (
          <p className="text-sm text-seagreen-700">
            Award created: {scr(award.monthly)}/month from {formatDate(award.startDate)}, next review {formatDate(award.reviewDate)}.
          </p>
        ) : (
          <>
            <Field label="Start date">
              <input className="field-input" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </Field>
            <Field label="Review after (months)">
              <input className="field-input" type="number" min={3} max={24} value={reviewAfterMonths} onChange={(e) => setReviewAfterMonths(Number.parseInt(e.target.value, 10))} />
            </Field>
            <Field label="Conditions (comma-separated)">
              <input className="field-input" value={conditions} onChange={(e) => setConditions(e.target.value)} />
            </Field>
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            {award ? 'Close' : 'Cancel'}
          </button>
          {!award ? (
            <button className="btn-primary" onClick={submit} disabled={busy}>
              {busy ? 'Approving…' : 'Approve & award'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}

function ReviewModal({ award, onClose, onDone }: { award: WelfareAward; onClose: () => void; onDone: () => void }) {
  const [findings, setFindings] = useState('');
  const [outcome, setOutcome] = useState<'CONTINUE' | 'ADJUST' | 'TERMINATE'>('CONTINUE');
  const [newMonthly, setNewMonthly] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/v1/welfare/awards/${award.id}/review`, {
        findings,
        outcome,
        newMonthly: outcome === 'ADJUST' ? Number.parseFloat(newMonthly) : undefined,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Review failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title={`Home visit review — award ${award.id}`} onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <Field label="Outcome">
          <select className="field-input" value={outcome} onChange={(e) => setOutcome(e.target.value as typeof outcome)}>
            <option value="CONTINUE">Continue as-is</option>
            <option value="ADJUST">Adjust amount</option>
            <option value="TERMINATE">Terminate</option>
          </select>
        </Field>
        {outcome === 'ADJUST' ? (
          <Field label="New monthly amount (SCR)">
            <input className="field-input" type="number" value={newMonthly} onChange={(e) => setNewMonthly(e.target.value)} />
          </Field>
        ) : null}
        <Field label="Findings">
          <textarea className="field-input" rows={3} value={findings} onChange={(e) => setFindings(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || findings.length < 5}>
            {busy ? 'Saving…' : 'Save review'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ThresholdsModal({ onClose }: { onClose: () => void }) {
  const [effectiveFrom, setEffectiveFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const [rows, setRows] = useState([
    { size: 1, threshold: 3000 },
    { size: 2, threshold: 4500 },
    { size: 3, threshold: 5800 },
    { size: 4, threshold: 6900 },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/v1/welfare/thresholds', { effectiveFrom, table: rows });
      setDone(true);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Could not set thresholds');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal title="Set means-test thresholds" onClose={onClose}>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        {done ? (
          <p className="text-sm text-seagreen-700">Threshold table saved, effective {formatDate(effectiveFrom)}.</p>
        ) : (
          <>
            <Field label="Effective from">
              <input className="field-input" type="date" value={effectiveFrom} onChange={(e) => setEffectiveFrom(e.target.value)} />
            </Field>
            <div className="field-label">Household size → monthly threshold (SCR)</div>
            {rows.map((r, i) => (
              <div key={r.size} className="flex items-center gap-2">
                <span className="w-16 text-xs text-slate-500">Size {r.size}</span>
                <input
                  className="field-input"
                  type="number"
                  value={r.threshold}
                  onChange={(e) => setRows(rows.map((x, j) => (j === i ? { ...x, threshold: Number.parseFloat(e.target.value) } : x)))}
                />
              </div>
            ))}
          </>
        )}
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            {done ? 'Close' : 'Cancel'}
          </button>
          {!done ? (
            <button className="btn-primary" onClick={submit} disabled={busy}>
              {busy ? 'Saving…' : 'Save'}
            </button>
          ) : null}
        </div>
      </div>
    </Modal>
  );
}
